import type { JSX } from "react";
import type { TurnOutput } from "./types";
import { formatDateWithZone } from "../../utils/formatDate";
import { formatJSON, getFeedbackColor, getFeedbackIcon } from "./turnDetailShared";
import { parseMcpDealSearchOutput, parseMcpDocQaOutput } from "./mcpAudit";
import McpDealSearchOutput from "./McpDealSearchOutput";
import McpDocQaOutput from "./McpDocQaOutput";

function RawContent({ output }: { output: TurnOutput }): JSX.Element {
  return (
    <div className="bg-gray-50 p-3 rounded-md">
      <pre className="text-sm text-gray-900 whitespace-pre-wrap">
        {formatJSON(output.output_content)}
      </pre>
    </div>
  );
}

/** The structured panel for an MCP search output row, or null for anything else. */
function mcpPanel(output: TurnOutput): JSX.Element | null {
  const dealSearch = parseMcpDealSearchOutput(output.output_type, output.output_content);
  if (dealSearch) return <McpDealSearchOutput data={dealSearch} />;
  const docQa = parseMcpDocQaOutput(output.output_type, output.output_content);
  if (docQa) return <McpDocQaOutput data={docQa} />;
  return null;
}

function OutputContent({ output }: { output: TurnOutput }): JSX.Element {
  // MCP search outputs get a structured rendering (the counts and the queries
  // behind them); anything else falls back to pretty-printed JSON.
  const mcp = mcpPanel(output);
  if (!mcp) return <RawContent output={output} />;
  return (
    <div>
      {mcp}
      <details className="mt-2">
        <summary className="text-xs text-gray-500 cursor-pointer">Raw JSON</summary>
        <RawContent output={output} />
      </details>
    </div>
  );
}

function OutputCard({ output }: { output: TurnOutput }): JSX.Element {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-lg font-medium text-gray-900">Output {output.turn_output_id}</h4>
          <p className="text-sm text-gray-500">
            Type: {output.output_type} • {formatDateWithZone(output.created_at)}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className={`text-lg ${getFeedbackColor(output.thumbs_up, output.thumbs_down)}`}>
            {getFeedbackIcon(output.thumbs_up, output.thumbs_down)}
          </span>
        </div>
      </div>

      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-500 mb-1">Output Content</label>
        <OutputContent output={output} />
      </div>

      {output.feedback_comment && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Feedback</label>
          <p className="text-sm text-gray-700 italic">"{output.feedback_comment}"</p>
        </div>
      )}
    </div>
  );
}

export default function TurnOutputsTab({ outputs }: { outputs: TurnOutput[] }): JSX.Element {
  return (
    <div className="space-y-4">
      {outputs.map((output) => (
        <OutputCard key={output.turn_output_id} output={output} />
      ))}
      {outputs.length === 0 && (
        <div className="text-center py-8 text-gray-500">No outputs found for this turn.</div>
      )}
    </div>
  );
}
